/**
 * Wallet Selection Step
 *
 * First step in the wallet modal - choose wallet type or select existing
 * Bank-grade UX with EverReal brand design system
 */

import {
  Fingerprint,
  HardDrive,
  Download,
  Plus,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { motion } from "framer-motion";
import { AnimatedCard, PulsingCard } from "@/components/brand/AnimatedCard";
import {
  useBrowserWallet,
  useBrowserWalletStore,
  useWalletContext,
} from "@nullaone/wallet-react";
import { useSessionManager } from "@/features/session";
import {
  isPRFSupported,
  MAX_LOCAL_WALLETS,
  MAX_LEDGER_WALLETS,
} from "@nullaone/wallet-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SearchInput } from "@/components/brand/BrandInput";

// Animation variants for staggered entry
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" as const },
  },
};

export function WalletSelectStep() {
  const wallet = useBrowserWallet();
  const { storedWallets, selectWallet, connecting, closeModal } = wallet;
  const { setModalStep } = useBrowserWalletStore();
  const { core } = useWalletContext();
  const sessionConnect = useSessionManager((s) => s.connect);
  const sessionSwitchWallet = useSessionManager((s) => s.switchWallet);
  const sessionStatus = useSessionManager((s) => s.status);
  const [prfSupported, setPrfSupported] = useState(true);
  const [walletCounts, setWalletCounts] = useState({ local: 0, ledger: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const webHIDSupported = core.ledger.isWebHIDSupported();

  const showSearch = storedWallets.length >= 7;

  const filteredWallets = useMemo(() => {
    if (!searchQuery.trim()) return storedWallets;
    const q = searchQuery.toLowerCase();
    return storedWallets.filter(
      (w) =>
        w.name?.toLowerCase().includes(q) ||
        w.address.toLowerCase().includes(q),
    );
  }, [storedWallets, searchQuery]);

  const skipStagger = storedWallets.length > 10;

  const updateScrollIndicators = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    isPRFSupported().then(setPrfSupported);
  }, []);

  // Refresh wallet counts when stored wallets change
  useEffect(() => {
    core.storage.getWalletCountByType().then(setWalletCounts);
  }, [core, storedWallets]);

  // Update scroll indicator when wallet list changes
  useEffect(() => {
    requestAnimationFrame(updateScrollIndicators);
  }, [storedWallets.length, searchQuery, updateScrollIndicators]);

  const handleSelectWallet = async (address: string) => {
    try {
      closeModal();
      if (sessionStatus === "active") {
        await sessionSwitchWallet(address, wallet);
      } else {
        await sessionConnect(address, wallet);
      }
    } catch {
      // Error handled by session manager
    }
  };

  // Check wallet limits
  const canCreateLocal = walletCounts.local < MAX_LOCAL_WALLETS;
  const canConnectLedger = walletCounts.ledger < MAX_LEDGER_WALLETS;

  return (
    <motion.div
      className="space-y-6 py-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Existing wallets — scrollable list with search */}
      {storedWallets.length > 0 && (
        <motion.div className="space-y-3" variants={itemVariants}>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Your Wallets
          </h3>
          {showSearch && (
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search wallets..."
              variant="filled"
              inputSize="sm"
            />
          )}
          <div className="relative">
            <div
              ref={scrollContainerRef}
              onScroll={updateScrollIndicators}
              className={cn(
                "space-y-2 overflow-y-auto wallet-scroll-container",
                showSearch ? "h-[300px]" : "max-h-[300px]",
              )}
            >
              {filteredWallets.map((wallet, index) => (
                <motion.div
                  key={wallet.id}
                  variants={skipStagger ? undefined : itemVariants}
                  custom={index}
                >
                  <AnimatedCard
                    variant="outlined"
                    enableLift
                    enableScale
                    glow="subtle"
                    onClick={
                      connecting
                        ? undefined
                        : () => handleSelectWallet(wallet.address)
                    }
                    className={
                      connecting ? "opacity-50 cursor-not-allowed" : ""
                    }
                  >
                    <div className="flex items-center gap-4 p-4">
                      {/* Icon with gradient background */}
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/20 border border-border shrink-0">
                        {wallet.type === "ledger" ? (
                          <HardDrive className="h-6 w-6 text-primary" />
                        ) : (
                          <Fingerprint className="h-6 w-6 text-primary" />
                        )}
                      </div>

                      {/* Wallet info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {wallet.name ||
                            `Wallet ${wallet.address.slice(0, 8)}`}
                        </p>
                        <p className="text-sm text-muted-foreground font-mono">
                          {wallet.address.slice(0, 4)}...
                          {wallet.address.slice(-4)}
                        </p>
                      </div>

                      {/* Backup warning badge - clickable to open backup flow */}
                      {!wallet.backedUp && wallet.type === "local_webauthn" && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await selectWallet(wallet.address);
                              setModalStep("backup");
                            } catch {
                              // Error handled by hook
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-300 shrink-0 hover:bg-amber-100 transition-colors cursor-pointer"
                        >
                          <AlertTriangle className="h-3 w-3 text-amber-600" />
                          <span className="text-xs text-amber-600 font-medium">
                            Backup
                          </span>
                        </button>
                      )}

                      {/* Chevron indicator */}
                      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    </div>
                  </AnimatedCard>
                </motion.div>
              ))}
              {filteredWallets.length === 0 && searchQuery && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No wallets match "{searchQuery}"
                </p>
              )}
            </div>
            {/* Fade gradient when more wallets below */}
            {canScrollDown && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background/90 to-transparent rounded-b-lg" />
            )}
          </div>
        </motion.div>
      )}

      {/* Limit message when local wallet limit reached */}
      {!canCreateLocal && storedWallets.length > 0 && (
        <motion.p
          variants={itemVariants}
          className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
        >
          Maximum {MAX_LOCAL_WALLETS} local wallets reached.
        </motion.p>
      )}

      {/* Create/Import options - only show if under limit */}
      {canCreateLocal && (
        <motion.div className="space-y-3" variants={itemVariants}>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Get Started
          </h3>

          <div className="grid gap-3">
            {/* Create New Wallet - Primary Action with Pulsing Effect */}
            <motion.div variants={itemVariants}>
              {prfSupported && !connecting ? (
                <PulsingCard
                  variant="premium"
                  pulseSpeed={2.5}
                  className="cursor-pointer"
                  onClick={() => setModalStep("create")}
                >
                  <div className="flex items-center gap-4 p-4">
                    {/* Gold gradient icon */}
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary/80 to-primary shadow-lg shadow-sm shrink-0">
                      <Plus className="h-6 w-6 text-background" />
                    </div>

                    <div className="flex-1">
                      <p className="font-semibold text-foreground">
                        Create New Wallet
                      </p>
                      <p className="text-sm text-primary/80">
                        Secured with TouchID/FaceID
                      </p>
                    </div>

                    <ChevronRight className="h-5 w-5 text-primary shrink-0" />
                  </div>
                </PulsingCard>
              ) : (
                <AnimatedCard
                  variant="glass"
                  enableLift={false}
                  enableScale={false}
                  className="opacity-50 cursor-not-allowed"
                >
                  <div className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary border border-border shrink-0">
                      <Plus className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-muted-foreground">
                        Create New Wallet
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Secured with TouchID/FaceID
                      </p>
                    </div>
                  </div>
                </AnimatedCard>
              )}
            </motion.div>

            {!prfSupported && (
              <motion.p
                variants={itemVariants}
                className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
              >
                WebAuthn PRF not supported. Use Chrome or Edge with a compatible
                authenticator.
              </motion.p>
            )}

            {/* Import Existing Wallet */}
            <motion.div variants={itemVariants}>
              <AnimatedCard
                variant="glass"
                enableLift
                glow="subtle"
                onClick={
                  !prfSupported || connecting
                    ? undefined
                    : () => setModalStep("import")
                }
                className={
                  !prfSupported || connecting
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary border border-border shrink-0">
                    <Download className="h-6 w-6 text-muted-foreground" />
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-foreground">
                      Import Existing Wallet
                    </p>
                    <p className="text-sm text-muted-foreground">
                      From recovery phrase or private key
                    </p>
                  </div>

                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>
              </AnimatedCard>
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Ledger option - available if under limit */}
      <motion.div className="space-y-3" variants={itemVariants}>
        {!canCreateLocal && (
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Hardware Wallet
          </h3>
        )}

        <div className="grid gap-3">
          {/* Ledger Connection */}
          {canConnectLedger ? (
            <motion.div variants={itemVariants}>
              <AnimatedCard
                variant="glass"
                enableLift
                glow="subtle"
                onClick={
                  !webHIDSupported || connecting
                    ? undefined
                    : () => setModalStep("ledger")
                }
                className={
                  !webHIDSupported || connecting
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
              >
                <div className="flex items-center gap-4 p-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary border border-border shrink-0">
                    <HardDrive className="h-6 w-6 text-muted-foreground" />
                  </div>

                  <div className="flex-1">
                    <p className="font-semibold text-foreground">
                      Connect Ledger
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Use your hardware wallet
                    </p>
                  </div>

                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </div>
              </AnimatedCard>
            </motion.div>
          ) : (
            <motion.p
              variants={itemVariants}
              className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
            >
              Maximum {MAX_LEDGER_WALLETS} Ledger wallets reached.
            </motion.p>
          )}

          {canConnectLedger && !webHIDSupported && (
            <motion.p
              variants={itemVariants}
              className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg"
            >
              WebHID not supported. Use Chrome or Edge to connect Ledger.
            </motion.p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
